import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHeader, StartCTA } from "@/components/site/primitives";

const reveal = {
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const cards = [
  ["MVP", "Prove the idea.", "A focused first version that shows the one thing that matters actually works."],
  ["PWA", "Build once. Reach people everywhere.", "An app-like experience that works across devices, no app store required."],
  ["Web Applications", "Turn a business process into software.", "Portals, dashboards, marketplaces and custom internal tools."],
  ["Websites", "From digital presence to interactive experience.", "Focused business sites through rich, interactive experiences."],
  ["Mobile Applications", "Put the product in their hands.", "Products designed around mobile users and the way they work."],
  ["Monetized Products", "Build the product and the mechanism that makes revenue possible.", "Payments, subscriptions and memberships designed in from the start."],
  ["Interactive Experiences", "Calculators. Portals. Intake systems. Booking systems. Tools.", "Software that does more than display information."],
];

export default function WhatWeBuildPage() {
  return (
    <div>
      <PageHeader
        label="WHAT WE BUILD"
        title={"Bring us the idea.\nWe'll figure out\nwhat it needs."}
        intro="AIROTECH MVP isn't limited to one type of software. We help determine what your idea should become — then we build it."
      />

      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="border-t border-[#141414]">
            {cards.map(([name, line, desc], i) => (
              <motion.div
                key={name}
                {...reveal}
                transition={{ ...reveal.transition, delay: (i % 3) * 0.05 }}
                className="group grid md:grid-cols-[1fr_1.3fr] gap-4 md:gap-12 py-10 md:py-14 border-b border-[#141414] items-start"
              >
                <div>
                  <div className="mono-label text-zinc-600 mb-4">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="font-heading font-bold tracking-tight text-3xl md:text-4xl text-white group-hover:translate-x-1 transition-transform duration-300">
                    {name}
                  </h3>
                </div>
                <div>
                  <p className="font-heading text-xl md:text-2xl text-cyan-400/90">{line}</p>
                  <p className="mt-3 text-base text-zinc-500 leading-relaxed max-w-lg">{desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...reveal} className="pt-12">
            <Link
              to="/services"
              data-testid="explore-services-link"
              className="group inline-flex items-center gap-2 border border-white/15 text-white font-medium rounded-full px-7 py-3.5 mono-label hover:border-cyan-400/50 hover:text-cyan-300 transition-colors duration-300"
            >
              Explore Services &amp; Add-ons
              <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </motion.div>
        </div>
      </section>

      <StartCTA />
    </div>
  );
}
